<template>
  <div class="wa-contact-info-panel">
    <!-- Info Panel Header: [X] Info. del contacto -->
    <div class="wa-info-header">
      <div class="d-flex align-items-center">
        <button
          class="btn wa-icon-btn me-3 p-0 text-white"
          @click="$emit('cerrar')"
          title="Cerrar info del contacto"
        >
          <i class="mdi mdi-close font-size-20"></i>
        </button>
        <span class="fw-semibold font-size-15 text-white">Info. del contacto</span>
      </div>
    </div>

    <!-- Scrollable Content with Clean Continuous Sections -->
    <SimpleBar class="wa-info-scroll" data-simplebar>
      <div class="wa-info-body">
        
        <!-- Section 1: Hero Profile (Banner + Avatar + Info + Actions) -->
        <div class="wa-info-section wa-info-hero-section">
          <div class="wa-info-cover-banner"></div>
          <div class="wa-info-avatar-wrapper">
            <!-- Dual Avatar Component (Muestra solo WhatsApp si es Varios) -->
            <CmpDualAvatar
              :avatar-cliente="chat.avatarCliente || null"
              :id-cliente="idCliente"
              :avatar-whatsapp="chat.avatarWhatsapp || null"
              :nombre="nombreCompleto"
              :inicial="inicial"
              :es-varios="esGenerico"
              size="hero"
            />
          </div>

          <!-- Name & Phone -->
          <h5 class="wa-info-name mb-1">{{ nombreCompleto }}</h5>
          <p v-if="!esGenerico" class="wa-info-phone text-muted mb-2">
            {{ chat.prefijo ? chat.prefijo + ' ' : '' }}{{ chat.numero }}
          </p>
          <p class="wa-info-subtitle text-muted mb-2">
            {{ esGenerico ? 'Contacto de WhatsApp • No registrado' : (chat.servicios_activos || 'Servicio de Internet • Cliente del Sistema') }}
          </p>

            <!-- Tags / Status Badges -->
          <div class="d-flex align-items-center justify-content-center gap-2 mb-3">
            <span v-if="chat.principal" class="badge bg-soft-warning text-warning font-size-11 px-2 py-1">
              <i class="mdi mdi-star me-1"></i>Principal
            </span>
            <span
              v-if="!esGenerico"
              class="badge font-size-11 px-2 py-1"
              :class="esClienteActivo ? 'bg-soft-success text-success' : 'bg-soft-danger text-danger'"
            >
              <i :class="esClienteActivo ? 'mdi mdi-check-circle-outline' : 'mdi mdi-close-circle-outline'" class="me-1"></i>
              {{ esClienteActivo ? 'Cliente Activo' : 'Inactivo' }}
            </span>
            <span v-else class="badge bg-soft-secondary text-secondary font-size-11 px-2 py-1">
              <i class="mdi mdi-account-question-outline me-1"></i>No registrado
            </span>
          </div>

          <!-- Quick Action Buttons (Ver Ficha, Asignar Cliente y Archivar) -->
          <div class="wa-info-actions-bar">
            <button class="wa-info-action-btn" @click="$emit('abrir-modal-asignar-cliente', chat)" :title="esGenerico ? 'Asignar a un cliente' : 'Cambiar / reasignar cliente'">
              <div class="wa-action-icon-circle">
                <i class="mdi mdi-account-arrow-right"></i>
              </div>
              <span class="wa-action-label">{{ esGenerico ? 'Asignar cliente' : 'Cambiar cliente' }}</span>
            </button>
            <router-link
              v-if="!esGenerico && idCliente"
              :to="{ name: 'tableroCliente', params: { id_cliente: idCliente } }"
              class="wa-info-action-btn"
              title="Ver tablero del cliente"
            >
              <div class="wa-action-icon-circle">
                <i class="mdi mdi-account-box-outline"></i>
              </div>
              <span class="wa-action-label">Ver tablero</span>
            </router-link>
            <button
              class="wa-info-action-btn"
              @click="esArchivado ? $emit('desarchivar-contacto', chat) : $emit('archivar-contacto', chat)"
              :title="esArchivado ? 'Desarchivar chat' : 'Archivar chat'"
            >
              <div class="wa-action-icon-circle">
                <i :class="esArchivado ? 'mdi mdi-archive-arrow-up-outline' : 'mdi mdi-archive-outline'"></i>
              </div>
              <span class="wa-action-label">{{ esArchivado ? 'Desarchivar' : 'Archivar' }}</span>
            </button>
          </div>
        </div>

        <!-- Section: Resumen de cuenta (igual que las tarjetas del tablero) -->
        <div v-if="!esGenerico && idCliente" class="wa-info-section">
          <div class="d-flex align-items-center justify-content-between mb-3">
            <span class="wa-section-title">Resumen de cuenta</span>
            <span v-if="cargandoResumen" class="spinner-border spinner-border-sm text-muted"></span>
          </div>
          <div class="wa-detail-row">
            <span class="wa-detail-label">Cobro pendiente:</span>
            <span class="wa-detail-value fw-semibold">
              {{ formatoMoneda(resumen.pendiente) }}
              <span class="badge rounded-pill badge-soft-success ms-1">Por cobrar</span>
            </span>
          </div>
          <div class="wa-detail-row">
            <span class="wa-detail-label">Último pago:</span>
            <span class="wa-detail-value fw-semibold">
              {{ formatoMoneda(resumen.ultimoPago.importe) }}
              <span v-if="resumen.ultimoPago.fecha" class="badge rounded-pill badge-soft-success ms-1">{{ formatoFecha(resumen.ultimoPago.fecha) }}</span>
            </span>
          </div>
          <div class="wa-detail-row">
            <span class="wa-detail-label">Abono sin asignar:</span>
            <span class="wa-detail-value fw-semibold">
              {{ formatoMoneda(resumen.abono) }}
              <span class="badge rounded-pill badge-soft-success ms-1">Saldo a favor</span>
            </span>
          </div>
        </div>

        <!-- Section: Información del cliente (igual que el tablero) -->
        <div v-if="!esGenerico" class="wa-info-section">
          <div class="d-flex align-items-center justify-content-between mb-3">
            <span class="wa-section-title">Información del cliente</span>
            <span v-if="cargandoCliente" class="spinner-border spinner-border-sm text-muted"></span>
          </div>

          <!-- Etiquetas -->
          <div v-if="cliente.etiquetas && cliente.etiquetas.length > 0" class="d-flex flex-wrap gap-1 mb-3">
            <span
              v-for="etiqueta in cliente.etiquetas"
              :key="etiqueta.id"
              class="badge text-truncate"
              style="max-width: 120px"
              :title="etiqueta.nombre"
              :style="{ backgroundColor: etiqueta.color, color: colorIdealTexto(etiqueta.color) }"
            >
              {{ etiqueta.nombre }}
            </span>
          </div>

          <div class="wa-detail-row" v-if="cliente.numero">
            <span class="wa-detail-label">Número de cliente:</span>
            <span class="wa-detail-value fw-semibold">{{ cliente.numero }}</span>
          </div>
          <div class="wa-detail-row" v-else>
            <span class="wa-detail-label">ID Cliente:</span>
            <span class="wa-detail-value fw-semibold">#{{ idCliente }}</span>
          </div>

          <div class="wa-detail-row" v-if="direccion && tieneAlias('Calle')">
            <span class="wa-detail-label">Dirección:</span>
            <span class="wa-detail-value">{{ direccion }}</span>
          </div>
          <div class="wa-detail-row" v-if="telefonosCadena && tieneAlias('Teléfonos')">
            <span class="wa-detail-label">{{ cargarAliasCampo('Teléfonos') }}:</span>
            <span class="wa-detail-value">{{ telefonosCadena }}</span>
          </div>
          <div class="wa-detail-row" v-if="cliente.correo && tieneAlias('Correo electrónico')">
            <span class="wa-detail-label">{{ cargarAliasCampo('Correo electrónico') }}:</span>
            <span class="wa-detail-value">{{ cliente.correo }}</span>
          </div>
          <div class="wa-detail-row" v-if="cliente.messenger && tieneAlias('Messenger')">
            <span class="wa-detail-label">{{ cargarAliasCampo('Messenger') }}:</span>
            <a
              class="wa-detail-value"
              :href="cliente.messenger.substring(0, 4) == 'http' ? cliente.messenger : `https://facebook.com/${cliente.messenger}`"
              target="_blank"
              rel="noopener noreferrer"
            >{{ cliente.messenger }}</a>
          </div>
          <div class="wa-detail-row" v-if="cliente.url1 && tieneAlias('URL 1')">
            <span class="wa-detail-label">{{ cargarAliasCampo('URL 1') }}:</span>
            <router-link
              class="wa-detail-value"
              :href="cliente.url1.substring(0, 4) == 'http' ? cliente.url1 : `https://${cliente.url1}`"
              rel="noopener noreferrer"
            >
              {{ cliente.url1 }}
            </router-link>
          </div>
          <div class="wa-detail-row" v-if="cliente.url2 && tieneAlias('URL 2')">
            <span class="wa-detail-label">{{ cargarAliasCampo('URL 2') }}:</span>
            <router-link
              class="wa-detail-value"
              :href="cliente.url2.substring(0, 4) == 'http' ? cliente.url2 : `https://${cliente.url2}`"
              rel="noopener noreferrer"
            >
              {{ cliente.url2 }}
            </router-link>
          </div>
          <div class="wa-detail-row" v-if="tieneAlias('Tipo de cliente') && cliente.id">
            <span class="wa-detail-label">{{ cargarAliasCampo('Tipo de cliente') }}:</span>
            <span class="wa-detail-value">{{ cliente.empresa == true ? 'EMPRESA' : 'Persona' }}</span>
          </div>
          <div class="wa-detail-row" v-for="aux in datosAuxiliares" :key="aux.campo">
            <span class="wa-detail-label">{{ aux.etiqueta }}:</span>
            <span class="wa-detail-value">{{ aux.valor }}</span>
          </div>
          <div class="wa-detail-row" v-if="chat.servicios_activos">
            <span class="wa-detail-label">Servicio Activo:</span>
            <span class="wa-detail-value text-success fw-medium">{{ chat.servicios_activos }}</span>
          </div>
          <div class="wa-detail-row d-block" v-if="cliente.nota && tieneAlias('Nota')">
            <span class="wa-detail-label d-block mb-1">{{ cargarAliasCampo('Nota') }}:</span>
            <span class="wa-detail-value text-start d-block">{{ cliente.nota }}</span>
          </div>

          <div class="mt-3">
            <router-link
              :to="{ name: 'tableroCliente', params: { id_cliente: idCliente } }"
              class="btn btn-outline-primary btn-sm w-100 d-flex align-items-center justify-content-center text-decoration-none"
            >
              <i class="mdi mdi-open-in-new me-1 font-size-14"></i>
              Abrir tablero completo del cliente
            </router-link>
          </div>
        </div>

        <!-- Section: Acciones de peligro -->
        <div class="wa-info-section wa-info-section-last">
          <button v-if="!Boolean(Number(chat.bloqueado))" class="wa-info-danger-btn text-danger w-100 mb-2" :disabled="procesandoBloqueo" @click="$emit('bloquear-contacto', chat)">
            <i class="mdi me-2 font-size-18" :class="procesandoBloqueo ? 'mdi-loading mdi-spin' : 'mdi-block-helper'"></i>
            Bloquear número
          </button>
          <button v-else class="wa-info-danger-btn text-success w-100 mb-2" :disabled="procesandoBloqueo" @click="$emit('desbloquear-contacto', chat)">
            <i class="mdi me-2 font-size-18" :class="procesandoBloqueo ? 'mdi-loading mdi-spin' : 'mdi-lock-open-outline'"></i>
            Desbloquear número
          </button>
          <button class="wa-info-danger-btn text-warning w-100 mb-2" @click="$emit('vaciar-chat', chat)">
            <i class="mdi mdi-broom me-2 font-size-18"></i>
            Vaciar chat
          </button>
          <button class="wa-info-danger-btn text-danger w-100 mb-2" @click="$emit('eliminar-chat', chat)">
            <i class="mdi mdi-chat-remove-outline me-2 font-size-18"></i>
            Eliminar chat
          </button>
          <button class="wa-info-danger-btn text-danger w-100" @click="$emit('eliminar-telefono-cliente', chat)">
            <i class="mdi mdi-phone-remove-outline me-2 font-size-18"></i>
            Eliminar número de teléfono
          </button>
        </div>

      </div>
    </SimpleBar>
  <br/>
  </div>
</template>

<script>
import { SimpleBar } from "simplebar-vue3";
import CmpDualAvatar from "./CmpDualAvatar.vue";
import ClienteSrv from "@/services/ClienteSrv.js";
import accounting from "accounting/accounting.js";
import moment from "moment";
import { todoGetters } from "@/state/helpers";

export default {
  name: "CmpInfoContacto",
  components: {
    SimpleBar,
    CmpDualAvatar
  },
  props: {
    chat: {
      type: Object,
      required: true
    },
    visible: {
      type: Boolean,
      default: false
    },
    procesandoBloqueo: {
      type: Boolean,
      default: false
    }
  },
  emits: [
    "cerrar",
    "ir-a-detalle-cliente",
    "archivar-contacto",
    "desarchivar-contacto",
    "vaciar-chat",
    "bloquear-contacto",
    "desbloquear-contacto",
    "eliminar-chat",
    "abrir-modal-asignar-cliente",
    "eliminar-telefono-cliente"
  ],
  data() {
    return {
      cliente: { id: null, etiquetas: [], telefonos: [] },
      cargandoCliente: false,
      cargandoResumen: false,
      resumen: { pendiente: 0, ultimoPago: {}, abono: 0 },
      idClienteCargado: null
    };
  },
  computed: {
    ...todoGetters,
    datosAuxiliares() {
      const lista = [];
      for (let n = 1; n <= 4; n++) {
        const valor = this.cliente && this.cliente["datoaux" + n];
        const nombre = "Dato auxiliar " + n;
        if (valor && this.tieneAlias(nombre)) {
          lista.push({ campo: "datoaux" + n, etiqueta: this.cargarAliasCampo(nombre), valor });
        }
      }
      return lista;
    },
    esArchivado() {
      return Boolean(Number(this.chat?.archivado));
    },
    esGenerico() {
      if (!this.chat) return true;
      if (this.chat.cliente) {
        if (this.chat.cliente.generico) return true;
        const nom = (this.chat.cliente.nombre || "").toLowerCase().trim();
        if (nom === "varios" || nom === "publico en general" || nom === "público en general") return true;
      }
      const nomChat = (this.chat.nombre || "").toLowerCase().trim();
      if (nomChat === "varios") return true;
      return false;
    },
    esClienteActivo() {
      if (!this.chat) return true;
      if (this.chat.cliente && this.chat.cliente.activo !== undefined && this.chat.cliente.activo !== null) {
        return this.chat.cliente.activo === true || this.chat.cliente.activo === 1 || this.chat.cliente.activo === "1";
      }
      if (this.chat.cliente_activo !== undefined && this.chat.cliente_activo !== null) {
        return this.chat.cliente_activo === true || this.chat.cliente_activo === 1 || this.chat.cliente_activo === "1";
      }
      if (this.chat.activo !== undefined && this.chat.activo !== null) {
        return this.chat.activo === true || this.chat.activo === 1 || this.chat.activo === "1";
      }
      return true;
    },
    nombreCompleto() {
      if (this.esGenerico) {
        return (this.chat.prefijo ? this.chat.prefijo + " " : "") + (this.chat.numero || "Sin número");
      }
      if (this.chat.cliente && this.chat.cliente.nombre) {
        return `${this.chat.cliente.nombre} ${this.chat.cliente.apellido_paterno || ""} ${this.chat.cliente.apellido_materno || ""}`.trim();
      }
      return this.chat.nombre || this.chat.numero || "Sin nombre";
    },
    inicial() {
      if (this.chat.cliente && this.chat.cliente.nombre) {
        return this.chat.cliente.nombre.charAt(0).toUpperCase();
      }
      if (this.chat.nombre) return this.chat.nombre.charAt(0).toUpperCase();
      return "C";
    },
    direccion() {
      const c = this.cliente && this.cliente.id ? this.cliente : this.chat.cliente;
      if (c) {
        const partes = [
          c.calle,
          c.numero_exterior ? `Ext. ${c.numero_exterior}` : null,
          c.numero_interior ? `Int. ${c.numero_interior}` : null,
          c.colonia,
          c.municipio,
          c.codigo_postal ? `C.P. ${c.codigo_postal}` : null,
          c.estado_residencia,
          c.pais
        ].filter(Boolean);
        if (partes.length) return partes.join(", ");
      }
      return this.chat.direccion_cliente || null;
    },
    telefonosCadena() {
      const tels = (this.cliente && this.cliente.telefonos) || [];
      return tels.map((t) => t.numero).filter(Boolean).join(", ");
    },
    idCliente() {
      if (this.chat?.id_cliente) return this.chat.id_cliente;
      if (this.chat?.cliente?.id) return this.chat.cliente.id;
      return null;
    },
    notas() {
      return (this.chat.cliente && this.chat.cliente.nota) || this.chat.notas || null;
    }
  },
  mounted() {
    if (this.visible) {
      this.cargarCliente();
    }
  },
  watch: {
    visible(esVisible) {
      if (esVisible) {
        this.cargarCliente();
      }
    },
    idCliente() {
      this.idClienteCargado = null;
      if (this.visible) {
        this.cargarCliente();
      }
    }
  },
  methods: {
    cargarCliente() {
      if (!this.visible) return;
      const id = this.idCliente;
      if (!id || this.esGenerico) return;
      if (this.idClienteCargado === id && this.cliente && this.cliente.id === id) return;

      this.idClienteCargado = id;
      this.cargarResumen();
      this.cliente = { id: null, etiquetas: [], telefonos: [] };
      const solicitado = id;
      this.cargandoCliente = true;
      ClienteSrv.clienteJSON(id)
        .then((response) => {
          if (solicitado !== this.idCliente) return;
          const c = response.data || {};
          if (!c.telefonos) c.telefonos = [];
          if (!c.etiquetas) c.etiquetas = [];
          this.cliente = c;
        })
        .catch((error) => {
          console.log(error);
        })
        .finally(() => {
          if (solicitado === this.idCliente) this.cargandoCliente = false;
        });
    },
    cargarResumen() {
      this.resumen = { pendiente: 0, ultimoPago: {}, abono: 0 };
      const id = this.idCliente;
      if (!id || this.esGenerico) return;
      this.cargandoResumen = true;
      const vigente = () => id === this.idCliente;
      Promise.all([
        ClienteSrv.cobroPendiente(id).then((r) => { if (vigente()) this.resumen.pendiente = r.data >= 0 ? r.data : 0; }),
        ClienteSrv.ultimoPagoJSON(id).then((r) => { if (vigente()) this.resumen.ultimoPago = r.data || {}; }),
        ClienteSrv.abonoDisponible(id).then((r) => { if (vigente()) this.resumen.abono = r.data >= 0 ? r.data : 0; })
      ])
        .catch((error) => console.log(error))
        .finally(() => {
          if (vigente()) this.cargandoResumen = false;
        });
    },
    formatoMoneda(numero) {
      const m = (this.$store && this.$store.state.todo.moneda_sistema) || {};
      return accounting.formatMoney(numero || 0, { symbol: m.simbolo || "$", miles: ",", decimal: "." }) + " " + (m.codigo || "");
    },
    formatoFecha(fecha) {
      return moment(fecha, "YYYY-MM-DD HH:mm:ss").format("DD/MM/YYYY");
    },
    colorIdealTexto(hexColor) {
      if (!hexColor || hexColor.length < 7) return "#ffffff";
      const r = parseInt(hexColor.substring(1, 3), 16);
      const g = parseInt(hexColor.substring(3, 5), 16);
      const b = parseInt(hexColor.substring(5, 7), 16);
      const delta = r * 0.299 + g * 0.587 + b * 0.114;
      return 255 - delta < 105 ? "#000000" : "#ffffff";
    }
  }
};
</script>
